// What the native shell can do for the page, stamped onto `window` BEFORE the
// page's own scripts run (the WebView's `injectedJavaScriptBeforeContentLoaded`),
// so every render already knows whether a Scan button or an in-app checkout
// path exists instead of discovering it one message later.
//
// The desktop/Playwright `?embed=native` rehearsal has no shell, so it reads
// `null` here and the web falls back to DEFAULT_NATIVE_CAPABILITIES — unless a
// test installs the global itself with
// `page.addInitScript(buildCapabilitiesScript(...))`, which is why the builder
// lives in this package and not in the mobile app.

import type { NativeHostPlatform } from './hostDetection';
import { NATIVE_BRIDGE_VERSION } from './protocol';

export type NativeCheckoutMode = 'web' | 'none';

export interface NativeCapabilities {
  platform: NativeHostPlatform;
  /** Apple RoomPlan works here: a LiDAR iPhone/iPad, kill switch off. */
  scan: boolean;
  /** `web`: the app may open web checkout in an in-app Safari sheet — only on
   *  App Store storefronts that allow an external purchase link (the US, ADR
   *  009). `none`: no purchase path inside the app. */
  checkout: NativeCheckoutMode;
}

export const NATIVE_CAPABILITIES_GLOBAL = '__RT_NATIVE__' as const;

/** Stamped on `<html>` with the platform so CSS can target app-only chrome. */
export const NATIVE_HTML_ATTRIBUTE = 'data-native' as const;

/** Stamped on `<html>` with `checkout`, so CSS can hide a purchase affordance
 *  before the page's first frame, not one hydration later. */
export const NATIVE_CHECKOUT_HTML_ATTRIBUTE = 'data-native-checkout' as const;

export const DEFAULT_NATIVE_CAPABILITIES: Readonly<Omit<NativeCapabilities, 'platform'>> = Object.freeze({
  scan: false,
  checkout: 'none',
});

const CHECKOUT_MODES: readonly NativeCheckoutMode[] = ['web', 'none'];
const PLATFORMS: readonly NativeHostPlatform[] = ['ios', 'android'];

/** Script for `injectedJavaScriptBeforeContentLoaded` (and Playwright's
 *  `addInitScript`). Same double-JSON quoting as `buildInjectScript`. */
export function buildCapabilitiesScript(caps: NativeCapabilities): string {
  const payload = JSON.stringify({ v: NATIVE_BRIDGE_VERSION, ...caps });
  const key = JSON.stringify(NATIVE_CAPABILITIES_GLOBAL);
  const attr = JSON.stringify(NATIVE_HTML_ATTRIBUTE);
  const checkoutAttr = JSON.stringify(NATIVE_CHECKOUT_HTML_ATTRIBUTE);
  const platform = JSON.stringify(caps.platform);
  const checkout = JSON.stringify(caps.checkout);
  return (
    `(function(){try{window[${key}]=Object.freeze(JSON.parse(${JSON.stringify(payload)}));` +
    `var el=document.documentElement;if(el){el.setAttribute(${attr},${platform});` +
    `el.setAttribute(${checkoutAttr},${checkout});}}catch(e){}})();true;`
  );
}

export function parseNativeCapabilities(raw: unknown): NativeCapabilities | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (r.v !== NATIVE_BRIDGE_VERSION) return null;
  if (typeof r.platform !== 'string' || !(PLATFORMS as readonly string[]).includes(r.platform)) return null;
  if (typeof r.scan !== 'boolean') return null;
  if (typeof r.checkout !== 'string' || !(CHECKOUT_MODES as readonly string[]).includes(r.checkout)) return null;
  return {
    platform: r.platform as NativeHostPlatform,
    scan: r.scan,
    checkout: r.checkout as NativeCheckoutMode,
  };
}

/** The shell's announcement, or `null` with no shell (or a mismatched one). */
export function readNativeCapabilities(win: object): NativeCapabilities | null {
  return parseNativeCapabilities((win as Record<string, unknown>)[NATIVE_CAPABILITIES_GLOBAL]);
}
